import * as THREE from 'three';
import { Visualizer } from '../visualizer.js';
import { FeedbackBuffer } from '../feedback-buffer.js';
import { safeZoneUniforms } from '../shaders/safeZone.glsl.js';
import { vdjColor, VdjPost, VdjComfort, VDJ_PRESETS } from './lib/vdjLook.js';

// Contrat du rig partagé : interdit. L'encre est sa propre lumière.
const RIG = 'forbidden';
export { RIG };

export const PROFILE = {
  id: 'ink',
  family: 'organic',
  danceability: 0.45,
  requiresTier: 0,
  energyLevel: 0.32,
  bpmMin: 60,
  bpmMax: 115,
  movement: 0.30,
  tonality: 0.85,
  enabled: true
};

// Ink : des gouttes d'encre lumineuse tombent dans une eau noire et
// descendent en PANACHES. Ambient rythmée, néo-classique électronique,
// trip-hop, soul lente. Règles de kiosk-app/CLAUDE.md ; danceability 0,45 ->
// preset LOUNGE (R9) : aucune impulsion sur les temps (une horloge continue,
// aucune phase de temps n'entre dans l'image), aucun flash, luminance
// moyenne sous 0,35 (R10).
//
// LA GOUTTE (v2, corrections du 14/10/2026) : une forme ANALYTIQUE de son
// âge, calculée sur le CPU (positions) et dessinée par le GPU (densités) :
//  - la TÊTE est un anneau tourbillonnaire vu de côté : une bande elliptique
//    aplatie qui s'élargit et descend, plus épaisse à ses deux bouts (là où
//    le regard traverse le plus de tore) ; autour de ces deux bouts, le
//    repère du bruit TOURNE (enroulement), d'un angle borné ;
//  - la TIGE : quelques filaments fins verticaux qui pendent au-dessus de la
//    tête, jusqu'au point de chute, et s'effacent avec l'âge ;
//  - après 6 mesures la tête se SCINDE en 2 ou 3 têtes plus petites qui
//    s'écartent en descendant.
// La chute DÉPLACE LE CENTRE de la forme, elle ne fait jamais défiler le
// bruit : le bruit est évalué dans le repère de la goutte, qui tourne
// lentement. Le domain warp est borné à 0,45 fois la longueur d'onde du bruit
// qu'il déforme (au-delà : des traînées et des stries - c'était la v1, qui
// faisait couler les UV vers le bas). La descente ralentit (exponentielle)
// et s'arrête pour que tout le panache reste au-dessus de 80 % de la hauteur.
//
// DEUX PASSES (R2) :
//  1. DENSITÉS BRUTES en demi-résolution (quart au tier 0), RGBA8 en
//     LinearFilter : r = le VOILE (large, basse fréquence), g/b = les
//     FILAMENTS sur 16 bits (octet haut / octet bas - l'interpolation
//     linéaire des deux octets est exactement celle de la valeur ; le Pi rend
//     dans un flottant mais ne sait pas le filtrer). Aucun seuil ici.
//  2. EN PLEINE RÉSOLUTION, après l'agrandissement linéaire : l'OPACITÉ
//     continue alpha = 1 - exp(-(kv voile + kf filaments)) - jamais de seuil,
//     des bords toujours progressifs, un creux reste un voile plus clair ; la
//     COULEUR (moyenne géométrique des encres pondérée par leurs enveloppes
//     analytiques : deux encres qui se croisent se multiplient à moitié) ;
//     l'ÉCLAIRAGE DIFFUS, sans reflet : les zones minces plus claires et plus
//     saturées (la lumière les traverse), les zones épaisses plus sombres au
//     coeur et éclairées du côté de la lumière (gradient d'épaisseur, atténué).

const TIER = {
  0: { scale: 0.25, drops: 4 },
  1: { scale: 0.5, drops: 6 },
  2: { scale: 0.5, drops: 6 }
};
const HEADS = 3;                          // têtes par goutte (après la division)
const LIFE_BARS = 24, FADE_FROM = 16;     // se déploie, puis s'efface de 16 à 24 mesures
const SPLIT_FROM = 6, SPLIT_TO = 10;      // la division de la tête
const RING_R0 = 0.06, RING_GROW = 0.28;   // rayon de l'anneau (unités de hauteur, -1..1)
const FLOOR_Y = -0.6;                     // 80 % de la hauteur : rien en dessous
const KV = 0.43, KF = 1.9;                // opacités : voile max 0,35 ; coeurs des filaments ~0,9
const F_MAX = 2.0;                        // l'échelle d'encodage des filaments
const NOISE_SCALE = 7.0;                  // unités de bruit par unité d'écran
const FIL_FREQ = 1.6;                     // fréquence des filaments (dans le repère de bruit)
const WARP_AMP = 0.45 / FIL_FREQ;         // le domain warp : 0,45 longueur d'onde
const CURL_MAX = 1.3;                     // l'enroulement autour des bouts de l'anneau (radians)
const INK_GAIN = 0.95;
const SAFE_FLOOR = 0.5;
const LOUNGE = VDJ_PRESETS.lounge;
const DROP_SCORE_MIN = 0.7, DROP_STALE_MS = 1500;

function clamp01(x) { return Math.max(0, Math.min(1, x)); }
function smooth(e0, e1, x) { const t = clamp01((x - e0) / (e1 - e0)); return t * t * (3 - 2 * t); }
function urlParam(name) {
  if (typeof location === 'undefined') return null;
  try { return new URLSearchParams(location.search).get(name); } catch (e) { return null; }
}
function rng(seed) { let s = seed >>> 0; return () => { s = (s * 1664525 + 1013904223) >>> 0; return s / 4294967296; }; }

// LE PANACHE à un âge donné (en mesures) - pur calcul, testé dans Node.
// drop : { x, y (point de chute), n (2 ou 3 têtes), fall (descente totale), spread }
// Rend { heads: [{x, y, r, amp}], stem: {x, top, bottom, amp}, strength, curl }
export function panache(drop, ageBars) {
  const t = Math.max(0, ageBars);
  const strength = 1 - smooth(FADE_FROM, LIFE_BARS, t);
  const Rr = RING_R0 + RING_GROW * (1 - Math.exp(-t / 5));
  // la descente ralentit et s'arrête : 98 % du chemin à 24 mesures
  const yc = drop.y - drop.fall * (1 - Math.exp(-t / 6));
  const sp = smooth(SPLIT_FROM, SPLIT_TO, t);
  const n = drop.n;
  const heads = [];
  for (let k = 0; k < HEADS; k++) {
    if (k >= n) { heads.push({ x: 0, y: 0, r: 0, amp: 0 }); continue; }
    const c = k - (n - 1) / 2;
    // avant la division, les n têtes coïncident et se partagent la masse ;
    // après, chacune est plus petite, s'écarte et descend un peu plus
    heads.push({
      x: drop.x + c * Rr * drop.spread * sp,
      y: yc - 0.07 * sp * (1 - 0.5 * Math.abs(c)),
      r: Rr * (1 - 0.38 * sp),
      amp: (1 / n + (0.85 - 1 / n) * sp) * strength
    });
  }
  return {
    heads,
    stem: { x: drop.x, top: drop.y, bottom: yc, amp: strength * (0.35 + 0.65 * Math.exp(-t / 8)) },
    strength,
    curl: CURL_MAX * (1 - Math.exp(-t / 3))
  };
}
// le point le plus bas d'un panache (tête : demi-hauteur de l'ellipse + la bande)
export function basPanache(p) {
  let low = Infinity;
  for (const h of p.heads) if (h.amp > 0) low = Math.min(low, h.y - h.r * 0.45 - h.r * 0.5);
  return low;
}

// ------------------------------------------------------------- le bruit lisse

let NOISE = null;
function noiseTexture() {
  if (NOISE) return NOISE;
  const N = 256, L = 32, cell = N / L, data = new Uint8Array(N * N * 4);
  const r = rng(90210);
  const lattice = [0, 1, 2, 3].map(() => { const a = new Float32Array(L * L); for (let i = 0; i < a.length; i++) a[i] = r(); return a; });
  const q = (t) => t * t * t * (t * (t * 6 - 15) + 10);
  for (let y = 0; y < N; y++) for (let x = 0; x < N; x++) {
    const gx = x / cell, gy = y / cell, x0 = Math.floor(gx), y0 = Math.floor(gy), fx = q(gx - x0), fy = q(gy - y0);
    const x1 = (x0 + 1) % L, y1 = (y0 + 1) % L;
    for (let c = 0; c < 4; c++) {
      const a = lattice[c];
      const v00 = a[y0 * L + x0], v10 = a[y0 * L + x1], v01 = a[y1 * L + x0], v11 = a[y1 * L + x1];
      const v = (v00 + (v10 - v00) * fx) + ((v01 + (v11 - v01) * fx) - (v00 + (v10 - v00) * fx)) * fy;
      data[(y * N + x) * 4 + c] = Math.round(v * 255);
    }
  }
  const t = new THREE.DataTexture(data, N, N, THREE.RGBAFormat, THREE.UnsignedByteType);
  t.wrapS = t.wrapT = THREE.RepeatWrapping; t.minFilter = THREE.LinearFilter; t.magFilter = THREE.LinearFilter;
  t.generateMipmaps = false; t.colorSpace = THREE.NoColorSpace; t.needsUpdate = true;
  NOISE = t;
  return t;
}

// ------------------------------------------------------------------ shaders

const QUAD_VERTEX = `
  varying vec2 vUv;
  void main() { vUv = uv; gl_Position = vec4(position.xy * 2.0, 0.0, 1.0); }
`;
// tableaux d'uniforms DÉROULÉS en JS (le GPU du Pi lit un tableau indexé
// par une variable de boucle via l'unité de texture, à chaque pixel)
const SHAPE_UNIFORMS = (ND) => `
  uniform vec4 uHead[${ND * HEADS}];    // x, y, rayon de l'anneau, amplitude
  uniform vec4 uStem[${ND}];            // x, haut, bas, amplitude
  uniform vec4 uMeta[${ND}];            // graine, rotation du repère, enroulement, boîte (demi-largeur)
`;

function densityFragment(ND) {
  const drop = (i) => {
    const heads = Array.from({ length: HEADS }, (_, k) => `{
        vec4 h = uHead[${i * HEADS + k}];
        if (h.w > 0.0) {
          vec2 u = s - h.xy;
          float r = h.z;
          float sd = u.x >= 0.0 ? 1.0 : -1.0;
          // L'ANNEAU VU DE CÔTÉ : deux LOBES denses aux bouts (le tore coupé par
          // le regard), en tête de la descente ; chacun ENROULÉ : une spirale
          // analytique autour de son centre (le sens : vers l'extérieur et vers le
          // haut, comme un anneau qui descend)
          vec2 lc = h.xy + vec2(sd * r, 0.0);
          vec2 lu = (s - lc) / (0.42 * r);
          float lr = length(lu);
          float ang = atan(lu.y, lu.x * sd);
          float spiral = 0.5 + 0.5 * cos(ang - 5.5 * lr + m.z * 2.0);
          float lobe = exp(-lr * lr * 1.1);
          // la CLOCHE : un voile en dôme au-dessus de l'anneau, qui le relie à la tige
          float bell = exp(-sq(u.x / (0.85 * r))) * exp(-sq((u.y - 0.45 * r) / (0.6 * r))) * smoothstep(-0.25 * r, 0.2 * r, u.y);
          hf += h.w * (lobe * (0.45 + 0.55 * spiral) + 0.25 * bell);
          hv += h.w * (0.5 * lobe + 0.8 * bell);
          // l'enroulement du repère du bruit autour du lobe, borné
          float th = m.z * exp(-lr * lr * 0.8) * sd * min(h.w * 2.0, 1.0);
          if (abs(th) > abs(curl)) { curl = th; curlC = lc; }
        }
      }`).join('');
    return `{
      vec4 m = uMeta[${i}];
      vec4 st = uStem[${i}];
      vec4 h0 = uHead[${i * HEADS}];
      // la boîte de la goutte : rien à évaluer au-dehors
      if (st.w > 0.0 && abs(s.x - st.x) < m.w && s.y < st.y + 0.06 && s.y > h0.y - 0.6) {
        float hf = 0.0, hv = 0.0, curl = 0.0;
        vec2 curlC = vec2(0.0);
        ${heads}
        // la tige : trois filaments fins qui pendent au-dessus de la tête
        float stem = 0.0;
        if (s.y < st.y && s.y > st.z) {
          float up = clamp((s.y - st.z) / max(st.y - st.z, 0.02), 0.0, 1.0);
          // un filament principal doux et un compagnon plus fin, qui ondulent
          // lentement (bruit basse fréquence le long de la tige)
          float wig = (texture2D(uNoise, vec2(s.y * 0.35, m.x * 0.11)).a - 0.5) * 0.05;
          float wig2 = (texture2D(uNoise, vec2(s.y * 0.5 + 3.7, m.x * 0.17)).b - 0.5) * 0.06;
          stem = exp(-sq((s.x - st.x - wig) / 0.010)) + 0.45 * exp(-sq((s.x - st.x - wig2 - 0.025) / 0.006));
          stem *= st.w * (1.0 - up * up) * smoothstep(0.0, 0.10, s.y - st.z + 0.02);
        }
        if (hf + hv + stem > 0.002) {
          // LE REPÈRE DU BRUIT : celui de la goutte (centré sur sa tête,
          // tourné lentement), jamais défilant ; puis l'enroulement autour du
          // bout le plus proche
          vec2 sc = s;
          if (curl != 0.0) { vec2 d = sc - curlC; float c = cos(curl), sn = sin(curl); sc = curlC + vec2(c * d.x - sn * d.y, sn * d.x + c * d.y); }
          vec2 q = (sc - h0.xy) * ${NOISE_SCALE.toFixed(1)};
          float cr = cos(m.y), sr = sin(m.y);
          q = vec2(cr * q.x - sr * q.y, sr * q.x + cr * q.y) + vec2(m.x, m.x * 0.7);
          // le domain warp, BORNÉ à 0,45 longueur d'onde des filaments
          vec4 w = nz(q * 0.5);
          vec2 qw = q * ${FIL_FREQ.toFixed(2)} + (w.xy - 0.5) * 2.0 * ${(WARP_AMP * FIL_FREQ).toFixed(3)};
          float rd = ridged(qw);
          F += hf * rd * 1.4 + stem * (0.6 + 0.4 * rd);
          V += hv * (0.55 + 0.45 * w.a) + stem * 0.15;
        }
      }
    }`;
  };
  return `
  precision highp float;
  uniform sampler2D uNoise;
  uniform float uAspect;
  ${SHAPE_UNIFORMS(ND)}
  varying vec2 vUv;
  float sq(float x) { return x * x; }
  vec4 nz(vec2 p) { return texture2D(uNoise, p * 0.03125); }
  // ridged : des crêtes fines (les filaments), trois octaves, une lecture chacune
  float ridged(vec2 p) {
    float r = 1.0 - abs(2.0 * nz(p).r - 1.0); float s = 0.55 * r * r;
    r = 1.0 - abs(2.0 * nz(p * 2.03 + 7.3).g - 1.0); s += 0.30 * r * r;
    r = 1.0 - abs(2.0 * nz(p * 4.01 + 3.1).b - 1.0); s += 0.15 * r * r;
    return s;
  }
  void main() {
    vec2 s = (vUv * 2.0 - 1.0) * vec2(uAspect, 1.0);
    float V = 0.0, F = 0.0;
    ${Array.from({ length: ND }, (_, i) => drop(i)).join('\n')}
    // les filaments sur 16 bits : octet haut, octet bas
    float f16 = floor(clamp(F / ${F_MAX.toFixed(1)}, 0.0, 1.0) * 65535.0 + 0.5);
    float hi = floor(f16 / 256.0);
    gl_FragColor = vec4(clamp(V, 0.0, 1.0), hi / 255.0, (f16 - hi * 256.0) / 255.0, 1.0);
  }
  `;
}

function lightFragment(ND) {
  const weight = (i) => `{
      vec4 st = uStem[${i}];
      if (st.w > 0.0) {
        float w = 0.0;
        ${Array.from({ length: HEADS }, (_, k) => `{ vec4 h = uHead[${i * HEADS + k}]; if (h.w > 0.0) { vec2 u = (s - h.xy) / vec2(1.6 * h.z, 1.0 * h.z); w += h.w * exp(-dot(u, u)); } }`).join('\n        ')}
        w += st.w * 0.6 * exp(-sq((s.x - st.x) / 0.05)) * step(st.z, s.y) * step(s.y, st.y);
        ws += w;
        lc += w * log(max(uDropCol[${i}], vec3(0.02)));
      }
    }`;
  return `
  precision mediump float;
  uniform highp sampler2D uDen;
  uniform highp vec2 uTexel;
  uniform float uAspect;
  uniform float uExposure;
  uniform vec3 uDropCol[${ND}];
  ${SHAPE_UNIFORMS(ND)}
  uniform float uSafeAspect;
  uniform vec2 uSafeCenter;
  uniform vec2 uSafeHalfSize;
  uniform float uSafeFeather;
  varying vec2 vUv;
  float sq(float x) { return x * x; }
  float safeAtt(vec2 uv) {
    vec2 ndc = uv * 2.0 - 1.0;
    vec2 s = vec2(ndc.x * uSafeAspect, ndc.y);
    vec2 q = abs(s - uSafeCenter) - uSafeHalfSize;
    float d = length(max(q, 0.0)) + min(max(q.x, q.y), 0.0);
    return mix(${SAFE_FLOOR.toFixed(2)}, 1.0, smoothstep(0.0, uSafeFeather, d));
  }
  // l'épaisseur optique : voile + filaments (16 bits décodés en highp)
  float tau(highp vec2 uv) {
    highp vec4 t = texture2D(uDen, uv);
    highp float f = (t.g * 255.0 * 256.0 + t.b * 255.0) / 65535.0 * ${F_MAX.toFixed(1)};
    return ${KV.toFixed(2)} * t.r + ${KF.toFixed(2)} * f;
  }
  void main() {
    float T = tau(vUv);
    if (T < 0.0005) { gl_FragColor = vec4(0.0, 0.0, 0.0, 1.0); return; }
    // L'OPACITÉ CONTINUE : jamais de seuil, des bords toujours progressifs
    float alpha = 1.0 - exp(-T);
    // la couleur : les encres pondérées par leurs enveloppes analytiques,
    // moyenne géométrique (soustractif doux)
    vec2 s = (vUv * 2.0 - 1.0) * vec2(uAspect, 1.0);
    float ws = 0.0; vec3 lc = vec3(0.0);
    ${Array.from({ length: ND }, (_, i) => weight(i)).join('\n')}
    vec3 C = ws > 1e-4 ? exp(lc / ws) : vec3(0.5);
    // L'ÉCLAIRAGE DIFFUS : mince = traversé par la lumière, plus clair et
    // plus saturé ; épais = plus sombre au coeur, éclairé du côté de la
    // lumière (le gradient d'épaisseur, atténué). Aucun reflet.
    float thin = exp(-1.3 * T);
    float lum = dot(C, vec3(0.2126, 0.7152, 0.0722));
    vec3 thick = mix(C, vec3(lum), 0.3) * 0.45;
    vec3 body = mix(thick, C, thin);
    float gx = tau(vUv + vec2(1.5 * uTexel.x, 0.0)) - tau(vUv - vec2(1.5 * uTexel.x, 0.0));
    float gy = tau(vUv + vec2(0.0, 1.5 * uTexel.y)) - tau(vUv - vec2(0.0, 1.5 * uTexel.y));
    float side = clamp(dot(vec2(gx, gy), vec2(-0.55, 0.83)) * 1.2, -0.25, 0.30) * (1.0 - thin);
    vec3 col = body * alpha * (1.0 + side) * ${INK_GAIN.toFixed(2)};
    gl_FragColor = vec4(col * uExposure * safeAtt(vUv), 1.0);
  }
  `;
}

const MATERIALS = new Map();
function materials(ND) {
  if (MATERIALS.has(ND)) return MATERIALS.get(ND);
  const shape = {
    uHead: { value: Array.from({ length: ND * HEADS }, () => new THREE.Vector4()) },
    uStem: { value: Array.from({ length: ND }, () => new THREE.Vector4()) },
    uMeta: { value: Array.from({ length: ND }, () => new THREE.Vector4()) }
  };
  const aspect = { value: 1.78 };
  const den = new THREE.ShaderMaterial({
    uniforms: Object.assign({ uNoise: { value: noiseTexture() }, uAspect: aspect }, shape),
    vertexShader: QUAD_VERTEX, fragmentShader: densityFragment(ND), depthTest: false, depthWrite: false
  });
  const light = new THREE.ShaderMaterial({
    uniforms: Object.assign({
      uDen: { value: null }, uTexel: { value: new THREE.Vector2(1 / 640, 1 / 360) }, uExposure: { value: 1 }, uAspect: aspect,
      uDropCol: { value: Array.from({ length: ND }, () => new THREE.Color(1, 1, 1)) },
      uSafeAspect: safeZoneUniforms.uAspect, uSafeCenter: safeZoneUniforms.uSafeCenter, uSafeHalfSize: safeZoneUniforms.uSafeHalfSize, uSafeFeather: safeZoneUniforms.uSafeFeather
    }, shape),
    vertexShader: QUAD_VERTEX, fragmentShader: lightFragment(ND), depthTest: false, depthWrite: false
  });
  const m = { den, light, shape, aspect };
  MATERIALS.set(ND, m);
  return m;
}

// ------------------------------------------------------------- le visualiseur

export class InkVisualizer extends Visualizer {
  constructor(options) {
    super();
    const o = options || {};
    this._tier = TIER[o.qualityTier] ? o.qualityTier : 1;
    this._layout = TIER[this._tier];
    this._debugDrop = urlParam('inkdrop') === '1';
    const ag = parseFloat(urlParam('inkage'));
    // ?inkage=<f> : l'âge de la goutte de débogage, en fraction de 16 mesures
    // (0..1 le déploiement, 1..1,5 l'effacement)
    this._debugAge = Number.isFinite(ag) ? Math.max(0, ag) : null;
    this._probe = urlParam('inkprobe') === '1';
    this._post = new VdjPost({ bloomStrength: 0.3, bloomThreshold: 0.78, grain: 0.012, vignette: 0.35, aberration: 0.0012 });
    this._comfort = new VdjComfort(LOUNGE.comfort);
  }

  get postProcessing() { return this._post.value; }

  init(scene, camera) {
    this._scene = scene;
    const L = this._layout;
    const M = this._m = materials(L.drops);
    this._buffer = new FeedbackBuffer({ scale: L.scale, single: true });
    this._denScene = new THREE.Scene();
    this._denQuad = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), M.den);
    this._denQuad.frustumCulled = false;
    this._denScene.add(this._denQuad);
    this._ortho = new THREE.OrthographicCamera(-0.5, 0.5, 0.5, -0.5, 0, 10);
    this._ortho.position.z = 1;
    this._display = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), M.light);
    this._display.frustumCulled = false; this._display.renderOrder = -1000;
    const self = this;
    this._display.onBeforeRender = function (renderer) { self._render(renderer); };
    scene.add(this._display);

    this._rand = rng(31337);
    this._drops = [];
    this._side = this._rand() < 0.5 ? -1 : 1;
    this._lastAt = { '-1': null, '1': null };
    this._clock = 0; this._secondsBeat = 60 / 90; this._paused = false;
    this._phasePhrase = null; this._phrases = 0; this._free = 0;
    this._marksRef = undefined; this._dropMarks = []; this._dropIdx = 0; this._lastNowMs = null; this._lastMode = null; this._lastDropAt = -1e9;
    this._paletteKey = null; this._palette = null; this._colIdx = 0;
    this._applyPalette({ artworkPalette: null });
    if (this._debugDrop) {
      this._drops.push(this._newDrop({ x: -0.95, y: 0.55 }, this._palette[0], 0, 3));
    } else {
      // un écran déjà habité au démarrage : deux gouttes d'âges différents
      this._spawn(-5 * 4); this._spawn(-11 * 4);
    }
    this._writeUniforms();
    if (this._probe && typeof window !== 'undefined') window.__ink = this;
  }

  _applyPalette(profile) {
    const pal = profile.artworkPalette;
    const v = pal && pal.vividColors && pal.vividColors.length ? pal.vividColors : [0xff4fa0, 0x3fb6ff, 0xffb02b];
    const key = v.map(String).join('|');
    if (key === this._paletteKey) return;
    this._paletteKey = key;
    // R4 : saturation forcée (preset lounge 0,85), la teinte de la pochette gardée
    this._palette = v.map((c) => vdjColor(c, null, LOUNGE.saturation));
  }

  // trois couleurs à 0,25 de teinte au moins l'une de l'autre (R4) : celles de
  // la palette les plus écartées, complétées par la teinte la plus éloignée
  _threeColours() {
    const P = this._palette, hsl = { h: 0, s: 0, l: 0 };
    const hues = P.map((c) => { c.getHSL(hsl); return hsl.h; });
    const out = [P[0].clone()];
    const hd = (a, b) => Math.min(Math.abs(a - b), 1 - Math.abs(a - b));
    const chosen = [hues[0]];
    for (let k = 1; k < P.length && out.length < 3; k++) if (chosen.every((h) => hd(h, hues[k]) >= 0.25)) { out.push(P[k].clone()); chosen.push(hues[k]); }
    while (out.length < 3) {
      let best = 0, bestD = -1;
      for (let k = 0; k < 72; k++) { const h = k / 72, dmin = Math.min(...chosen.map((c) => hd(c, h))); if (dmin > bestD) { bestD = dmin; best = h; } }
      out.push(new THREE.Color().setHSL(best, LOUNGE.saturation, 0.55)); chosen.push(best);
    }
    return out;
  }

  // une goutte : son point de chute, 2 ou 3 têtes, sa descente totale
  // (calculée pour que tout le panache reste au-dessus de FLOOR_Y)
  _newDrop(at, col, birth, n) {
    const d = { x: at.x, y: at.y, birth, col: col.clone(), n: n || (this._rand() < 0.5 ? 2 : 3), spread: 1.1 + 0.4 * this._rand(), seed: this._rand() * 40, spin: (this._rand() - 0.5) * 0.02, fall: 0 };
    // la descente : bissection sur le point le plus bas atteint pendant la vie
    let lo = 0, hi = 2;
    for (let k = 0; k < 30; k++) { d.fall = (lo + hi) / 2; let low = Infinity; for (let t = 0; t <= LIFE_BARS; t += 1) low = Math.min(low, basPanache(panache(d, t))); if (low >= FLOOR_Y + 0.03) lo = d.fall; else hi = d.fall; }
    d.fall = lo;
    return d;
  }

  // R6 : à gauche puis à droite de la pochette, en alternance, depuis le
  // tiers supérieur ; le panache adulte entier dans le cadre (marge 5 %),
  // hors de la zone de sécurité et des zones d'interface ; jamais deux fois
  // au même endroit de suite sur un côté
  _place() {
    const asp = safeZoneUniforms.uAspect.value || 1.78;
    // R19 : de part et d'autre du bloc de scène, pas de la pochette
    const sc = safeZoneUniforms.uStageCenter.value, sh = safeZoneUniforms.uStageHalfSize.value;
    const side = this._side; this._side = -this._side;
    const halfW = (RING_R0 + RING_GROW) * 1.5 + 0.05;     // la tête adulte divisée, écartée
    const xMin = sh.x + 0.06 + halfW * 0.6, xMax = Math.max(xMin, asp * 0.95 - halfW);
    const prev = this._lastAt[side];
    let best = null;
    for (let k = 0; k < 30; k++) {
      const x = sc.x + side * (xMin + (xMax - xMin) * this._rand());
      const y = 0.42 + 0.26 * this._rand();                   // le tiers supérieur, sous la bande des boutons (12 %)
      if (prev && Math.hypot(prev.x - x, prev.y - y) < 0.3) continue;
      best = { x, y }; break;
    }
    best = best || { x: sc.x + side * (xMin + xMax) / 2, y: 0.55 };
    this._lastAt[side] = best;
    return best;
  }

  _spawn(birthOffsetBeats, col) {
    const c = col || this._palette[this._colIdx++ % this._palette.length];
    this._drops.push(this._newDrop(this._place(), c, this._clock + (birthOffsetBeats || 0)));
    while (this._drops.length > this._layout.drops) this._drops.shift();
  }

  _ageBars(d) {
    return this._debugDrop && this._debugAge != null ? this._debugAge * 16 : (this._clock - d.birth) / 4;
  }

  _writeUniforms() {
    const S = this._m.shape, ND = this._layout.drops, cols = this._m.light.uniforms.uDropCol.value;
    for (let i = 0; i < ND; i++) {
      const d = this._drops[i];
      if (!d) {
        S.uStem.value[i].set(0, 0, 0, 0);
        for (let k = 0; k < HEADS; k++) S.uHead.value[i * HEADS + k].set(0, 0, 0, 0);
        continue;
      }
      const age = this._ageBars(d), p = panache(d, age);
      for (let k = 0; k < HEADS; k++) { const h = p.heads[k]; S.uHead.value[i * HEADS + k].set(h.x, h.y, h.r, h.amp); }
      S.uStem.value[i].set(p.stem.x, p.stem.top, p.stem.bottom, Math.max(p.stem.amp, p.strength > 0 ? 1e-3 : 0));
      // la boîte : la tête divisée et écartée, plus la portée d'un lobe
      // (centre à r, gaussienne jusqu'à ~3 x 0,42 r au-delà) et l'enroulement.
      // Une boîte plus étroite (1,6 r) coupait les lobes en bords verticaux nets
      let half = 0.1;
      for (const h of p.heads) if (h.amp > 0) half = Math.max(half, Math.abs(h.x - d.x) + h.r * 2.7);
      S.uMeta.value[i].set(d.seed, d.spin * age * 4, p.curl, half);
      cols[i].copy(d.col);
    }
    if (!this._debugDrop) this._drops = this._drops.filter((d) => this._ageBars(d) < LIFE_BARS);
    this._m.aspect.value = safeZoneUniforms.uAspect.value || 1.78;
  }

  _tickDrops(profile) {
    const tl = profile.analysisTimeline;
    const marks = tl && tl.marks ? tl.marks : null;
    if (marks !== this._marksRef) {
      this._marksRef = marks; this._dropMarks = [];
      if (marks) for (const m of marks) if (m.type === 'drop' && m.score >= DROP_SCORE_MIN) this._dropMarks.push(m.atMs);
      this._dropIdx = 0;
    }
    const now = profile.estimatedPosition != null ? profile.estimatedPosition * 1000 : null;
    if (now == null) return false;
    if (this._lastNowMs != null && (now < this._lastNowMs - 1000 || now > this._lastNowMs + 5000)) this._dropIdx = 0;
    this._lastNowMs = now;
    let fired = false;
    while (this._dropIdx < this._dropMarks.length && this._dropMarks[this._dropIdx] <= now) {
      if (now - this._dropMarks[this._dropIdx] < DROP_STALE_MS) fired = true;
      this._dropIdx++;
    }
    return fired;
  }

  onTrackChange(profile) { this._applyPalette(profile); this._dropIdx = 0; this._lastNowMs = null; }

  update(deltaTime, profile) {
    const dt = Math.min(deltaTime, 0.1);
    this._applyPalette(profile);
    this._paused = profile.playbackState !== 'PLAYING';
    // repli sans BPM : 90
    const bpm = (profile.trackFeatures && profile.trackFeatures.bpm) || 0;
    this._secondsBeat = bpm > 0 ? 60 / THREE.MathUtils.clamp(bpm, 40, 200) : 60 / 90;
    // pause : tout gèle (l'horloge s'arrête), l'image reste
    if (!this._paused && !(this._debugDrop && this._debugAge != null)) {
      // UNE HORLOGE CONTINUE (dt / durée du temps) : aucune phase de temps
      // n'entre dans l'image - rien ne bouge sur les temps (R9 lounge)
      this._clock += dt / this._secondsBeat;
      // PHRASE : une nouvelle goutte, une phrase sur deux (repli : 16 temps)
      let phrase = false;
      const ph = profile.phrasePhase;
      if (ph != null) { if (this._phasePhrase != null && ph < this._phasePhrase - 0.5) phrase = true; this._phasePhrase = ph; }
      else { this._free += dt / this._secondsBeat; if (this._free >= 16) { this._free -= 16; phrase = true; } }
      if (phrase && !this._debugDrop) { this._phrases++; if (this._phrases % 2 === 0) this._spawn(0); }
      // DROP : trois gouttes simultanées, de trois couleurs (sans flash : lounge)
      const director = profile.director;
      let drop = this._tickDrops(profile);
      if (director && director.mode === 'drop' && this._lastMode !== 'drop' && director.hasReliableGrid) drop = true;
      this._lastMode = director ? director.mode : null;
      if (drop && !this._debugDrop && this._clock - this._lastDropAt > 16) {
        this._lastDropAt = this._clock;
        for (const c of this._threeColours()) this._spawn(0, c);
      }
    }
    this._writeUniforms();
    // R10 : luminance moyenne sous 0,35 (estimation : la surface des têtes
    // et des voiles x la luminance de l'encre)
    let cover = 0;
    for (const d of this._drops) { const p = panache(d, this._ageBars(d)); for (const h of p.heads) cover += h.amp * h.r * h.r * 2.5; }
    const est = clamp01(cover / (4 * (safeZoneUniforms.uAspect.value || 1.78))) * 0.3;
    this._m.light.uniforms.uExposure.value = this._comfort.measure(est, dt);
    this._post.update(1);
  }

  _render(renderer) {
    const prev = renderer.getRenderTarget();
    this._buffer.ensureSize(renderer);
    const rt = this._buffer.read;
    renderer.setRenderTarget(rt);
    renderer.render(this._denScene, this._ortho);
    renderer.setRenderTarget(prev);
    const U = this._m.light.uniforms;
    U.uDen.value = rt.texture;
    U.uTexel.value.set(1 / rt.width, 1 / rt.height);
  }

  getProbe() {
    return {
      tier: this._tier, clock: this._clock, phrases: this._phrases, exposure: this._m.light.uniforms.uExposure.value,
      rt: this._buffer.read ? [this._buffer.read.width, this._buffer.read.height] : null,
      drops: this._drops.map((d) => { const a = this._ageBars(d), p = panache(d, a); return { x: d.x, y: d.y, ageBars: a, n: d.n, heads: p.heads.filter((h) => h.amp > 0).map((h) => [h.x, h.y, h.r]), bas: basPanache(p) }; })
    };
  }

  dispose() {
    if (this._scene && this._display) { this._display.onBeforeRender = null; this._scene.remove(this._display); this._display.geometry.dispose(); }
    if (this._denQuad) this._denQuad.geometry.dispose();
    if (this._buffer) this._buffer.dispose();
    this._m.light.uniforms.uDen.value = null;
    if (typeof window !== 'undefined' && window.__ink === this) window.__ink = null;
    this._scene = null;
  }
}
